import { defineStore } from 'pinia'
import { useNetworkStore } from '@/stores/network/store'
import {
  transformIncomingExtension,
  transformOutgoingExtension,
  transformOutgoingForwarding,
  transformOutgoingForwardings,
} from '@/utils/dataTransformers'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaSuccess,
  waitFor,
  type Meta,
} from '@/utils/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useDevicesStore } from '@/stores/devices/store'
import { useDeviceStore } from '@/stores/device/store'
import { useSoftkeysStore } from '@/stores/softkeys/store'
import { usePromptsStore } from '@/stores/prompts/store'
import { useAudioFileStore } from '@/stores/audioFile/store'
import type { ColorName, ProfileColorName } from '@/utils/colors'

export type ExtendedForwardingStep = {
  ivrSelection: number
  id: number
  name: string
  forwardToExtensionId: string
  ringtime: number
  type: 'prompt' | 'external_number' | 'internal' | 'devices' | 'allDevices'
  devices: number[]
  externalPhoneNumbers: string[]
  promptId: number
  notificationChannel?: 'sms_and_email' | 'sms' | 'email' | null
  notificationType: 'none' | 'caller_heard_message' | 'caller_left_message'
  orderColumn: number
  color: string
  destination?: number
}

export type ExtendedForwardingProfile = {
  id: number
  name: string
  color: ProfileColorName
  isWaitingMusicEnabled: boolean
  extensionId: number
  waitingMusicId: number
  forwardings: ExtendedForwardingStep[]
  timeslots: {
    extendedForwardingProfileId: number
    day: number
    start: string
    end: string
    orderColumn: number
  }[]
  ivrTimeout?: number
  defaultIvrExtensionId?: number
}

export type ExtensionData = {
  color?: ColorName | null
  calleridForwardType: string
  conferencePin?: string | null
  directDial?: string
  email?: string
  forwardingType?: string | null
  id: number
  editable?: boolean
  isBudirectDialsyOnBusy?: boolean
  isBusyOnBusy?: boolean
  isClosingActive: boolean
  isSingleNumber?: boolean
  overrideEmailVerification?: boolean
  quickDial?: string
  setNameAsPrefix?: boolean
  showMissedCalls?: boolean
  sms_number?: string
  trunkId?: number | null
  type?: Api.Extension['type']
  queueData?: {
    waitingMusicId: number
    notificationChannel?: 'none' | 'sms_and_email' | 'sms' | 'email' | null
    queueTimeout: string
    announcePosition?: boolean
  }
  closing?: {
    createdAt: string
    extensionId: number
    forwardToExtensionId?: number | null
    id: number
    notificationChannel?: 'sms_and_email' | 'sms' | 'email' | null
    notificationType: 'none' | 'leftMessage' | 'heardMessage'
    period: number
    phoneNumber: string
    promptId: number
    trigger: string
    type: string
    updatedAt: string
  }
  // Ivr
  extendedForwardingProfiles?: ExtendedForwardingProfile[]
  forwardings?: SimpleForwarding
  devices?: number[]
  // Group
  userExtensions?: number[]
  name?: string
  firstname?: string
  lastname?: string
  e164?: string
  onlyForwardExternal?: boolean
  blockIncomingExternal?: boolean
  ignoreNightswitchInternal?: boolean
  allowRefer?: boolean
  groups?: Group[]
  externalReference: string | null
  fqdn?: string
}

export type Forwarding = {
  type: string
  promptId?: number
  period: number | undefined
  notificationChannel: string
  notificationChannelValue?: string
  notificationType: string
  phoneNumber: string
  forwardToExtensionId: number
}

export type SimpleForwarding = {
  general: Forwarding
  busy: Forwarding
  offline: Forwarding
}

export type Group = {
  id: number
  group_tag_id: number
  color: ColorName | ''
  label: string
}

type State = {
  extensionData?: ExtensionData
  meta: Meta<{
    extensionData: { id: string | number } | undefined
    load: { id: string | number }
    create: undefined
    update: { id: string | number }
    delete: { id: string | number }
    updateNightSwitch: { id: string | number }
    updateSimpleForwardings: { id: string | number }
  }>
}

const getDefaultState = (): State => ({
  extensionData: undefined,
  meta: createMeta([
    'extensionData',
    'load',
    'create',
    'update',
    'delete',
    'updateNightSwitch',
    'updateSimpleForwardings',
  ]),
})

export const useExtensionStore = defineStore('extension', {
  state: getDefaultState,

  getters: {
    get: (state) => state.extensionData,
  },

  actions: {
    RESET() {
      this.extensionData = getDefaultState().extensionData
      this.meta.extensionData = getDefaultState().meta.extensionData
    },

    async load(id: string | number, { useCache = true } = {}) {
      const hasCached =
        ['pending', 'success'].includes(this.meta.extensionData.status) &&
        Number(id) === Number(this.meta.extensionData.params?.id)

      if (useCache && hasCached) {
        await waitFor(() => this.meta.extensionData.status !== 'pending')

        return
      }

      const params = { id }

      await callWithMeta({
        store: this,
        metaFields: { load: params, extensionData: params },
        apiCall: async () => await Api.loadExtension(id),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('extensionData')) {
            this.extensionData = transformIncomingExtension(result.payload)
          }
        },
        onError: ({ result }) => {
          useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
        },
      })
    },

    async reload() {
      if (
        this.meta.extensionData.status !== 'initial' &&
        this.meta.extensionData.params
      ) {
        await this.load(this.meta.extensionData.params.id, { useCache: false })
      }
    },

    async create(data: any) {
      const transformedData = transformOutgoingExtension(data)

      if (transformedData.sms_number === null) {
        delete transformedData.sms_number
      }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { create: undefined, extensionData: undefined },
        apiCall: async () => await Api.createExtension(transformedData),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('create')) {
            useExtensionsStore().load({ useCache: false })

            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()

            useSoftkeysStore().RESET()
          }

          if (isLatest('extensionData')) {
            this.extensionData = transformIncomingExtension(result.payload)

            this.meta.extensionData = getMetaSuccess({
              id: result.payload.id,
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async update({ id, data }: any) {
      const params = { id }

      const isMigration = data?.migrate === true
      const transformData = { ...data }
      delete transformData.migrate
      const transformedData = transformOutgoingExtension(transformData)

      const { result } = await callWithMeta({
        store: this,
        metaFields: { update: params, extensionData: params },
        apiCall: async () => await Api.updateExtension(id, transformedData),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('update')) {
            if (!isMigration) {
              useExtensionsStore().load({ useCache: false })

              useDevicesStore().load({ useCache: false })

              useDeviceStore().reload()

              usePromptsStore().load({ useCache: false })

              useAudioFileStore().reload()
            }
          }

          if (isLatest('extensionData')) {
            this.extensionData = transformIncomingExtension(result.payload)
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async delete(id: string) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { delete: params, extensionData: params },
        apiCall: async () => await Api.deleteExtension(id),
        onSuccess: ({ isLatest }) => {
          if (isLatest('delete')) {
            useExtensionsStore().load({ useCache: false })

            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()

            useSoftkeysStore().RESET()
          }

          if (isLatest('extensionData')) {
            this.RESET()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async saveSimpleForwardings({ extensionId, forwardings }: any) {
      const responses = await Promise.all(
        forwardings.map(async (forwarding: any) => {
          if (forwarding.type === 'noAction') {
            if (forwarding.id) {
              const result = await Api.saveSimpleForwardings(
                'noAction',
                extensionId,
                forwarding
              )

              if (result.status === 'error') {
                throw result.payload
              }

              return result.payload
            }
          } else {
            if (forwarding.id) {
              const result = await Api.saveSimpleForwardings(
                'waitingMusic',
                extensionId,
                forwarding
              )

              if (result.status === 'error') {
                throw result.payload
              }

              return result.payload
            } else {
              const result = await Api.saveSimpleForwardings(
                'callForwarding',
                extensionId,
                forwarding
              )

              if (result.status === 'error') {
                throw result.payload
              }

              return result.payload
            }
          }
        })
      )

      return responses
        .filter((response) => response)
        .map((response) => response.data)
    },

    async updateSimpleForwardings({
      extensionId,
      forwardings,
      forwardingType,
    }: any) {
      const params = { id: extensionId }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { updateSimpleForwardings: params, extensionData: params },
        apiCall: async () => {
          const [, updatedForwardings] = await Promise.all([
            this.update({
              id: extensionId,
              data: {
                forwardingType: forwardingType,
              },
            }),
            this.saveSimpleForwardings({
              extensionId,
              forwardings: transformOutgoingForwardings(forwardings),
            }),
          ])

          return {
            status: 'success',
            payload: updatedForwardings,
          }
        },
        onSuccess: ({ isLatest }) => {
          if (isLatest('updateSimpleForwardings')) {
            useExtensionsStore().load({ useCache: false })

            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()
          }

          this.reload()
        },
      })

      return result.payload
    },

    async saveNightSwitch({ extensionId, nightSwitchData }: any) {
      const transformedNightSwitch =
        transformOutgoingForwarding(nightSwitchData)

      const result = await Api.updateNightSwitch(
        extensionId,
        transformedNightSwitch
      )

      if (result.status === 'error') {
        throw result.payload
      }

      return result.payload
    },

    async updateNightSwitch({
      extensionId,
      nightSwitchData,
      isClosingActive,
    }: any) {
      const params = { id: extensionId }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { updateNightSwitch: params, extensionData: params },
        apiCall: async () => {
          const [, updatedNightSwitch] = await Promise.all([
            this.update({
              id: extensionId,
              data: { isClosingActive: isClosingActive },
            }),
            this.saveNightSwitch({
              extensionId,
              nightSwitchData,
            }),
          ])

          return {
            status: 'success',
            payload: updatedNightSwitch,
          }
        },
        onSuccess: ({ isLatest }) => {
          if (isLatest('updateNightSwitch')) {
            useExtensionsStore().load({ useCache: false })

            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()
          }

          this.reload()
        },
      })

      return result.payload
    },
  },
})
